import { useEffect, useState } from "react";
import {
  removeBookmarkedPost,
  readBookmarkedPostIds,
  upsertBookmarkedPost,
  writeBookmarkedPostIds,
} from "../bookmarks";
import { getPostImageData } from "../utils/postMedia";

const truncateHTMLPreserve = (html, maxLength) => {
  if (!html) return "";

  let output = "";
  let charCount = 0;
  let insideTag = false;

  for (let i = 0; i < html.length; i++) {
    const char = html[i];

    if (char === "<") insideTag = true;

    if (!insideTag) {
      charCount++;
    }

    if (charCount > maxLength) break;

    output += char;

    if (char === ">") insideTag = false;
  }

  return charCount >= maxLength ? output + "..." : output;
};

function getCategoryArchivePath(categoryLink, categorySlug, fallbackLink) {
  if (categoryLink) {
    return categoryLink;
  }

  if (categorySlug) {
    return `/category/${categorySlug}/`;
  }

  return fallbackLink;
}

function getOptimizedImage(post) {
  return getPostImageData(post);
}

function getItourBrandStoryImageFallback(post) {
  const link = String(post?.link || "");
  if (!link.includes("/itour-rethinkways-luxury-honeymoon-chennai/")) return null;

  return {
    src: "https://travel.rethinkways.com/wp-content/uploads/2025/12/Travel_PR_iTour_Feat-02-1.jpg",
    alt: "iTour by Rethink Ways",
  };
}

function getPostCategoryTerms(post) {
  const termGroups = post?._embedded?.["wp:term"];
  if (!Array.isArray(termGroups)) return [];

  return termGroups
    .flat()
    .filter((term) => term?.taxonomy === "category" && term?.name && term?.slug);
}

const MIXED_CATEGORY_PRIORITY = [
  "attractions",
  "beaches",
  "islands",
  "waterfalls",
  "theme-parks",
  "parks",
  "wildlife",
  "nature",
  "adventure",
  "trekking",
  "camping",
  "hill-stations",
  "weekend-getaways",
  "hidden-gems",
  "world-wonders",
  "city",
  "desert",
  "festivals",
  "cruise",
  "culinary",
  "unique-stays",
  "visa-free",
  "visa-process",
  "travel-tips",
  "international-months",
  "india-months",
  "honeymoon-global",
  "honeymoon-india",
  "romantic-escapes",
  "family-getaways",
  "solo-travel",
  "friends",
  "india-2026",
  "fantasy",
  "season-stories",
];

function isHiddenMixedCategory(term) {
  const normalizedSlug = String(term?.slug || "").toLowerCase();
  const normalizedName = String(term?.name || "").toLowerCase();

  if (
    normalizedSlug === "latest-updates" ||
    normalizedSlug === "brand-story" ||
    (normalizedSlug.includes("brand") && normalizedSlug.includes("story"))
  ) {
    return true;
  }

  return (
    normalizedName === "brand story" ||
    (normalizedName.includes("brand") && normalizedName.includes("story")) ||
    normalizedSlug.includes("hospitality") ||
    normalizedSlug.includes("job") ||
    normalizedSlug.includes("tourism") ||
    normalizedName.includes("hospitality") ||
    normalizedName.includes("job") ||
    normalizedName.includes("tourism")
  );
}

function getMixedCategoryMeta(post) {
  const categories = getPostCategoryTerms(post).filter((term) => !isHiddenMixedCategory(term));
  const preferredCategory =
    MIXED_CATEGORY_PRIORITY.map((slug) => categories.find((term) => String(term.slug || "").toLowerCase() === slug)).find(Boolean) ||
    categories[0];

  if (!preferredCategory) {
    return null;
  }

  return {
    label: preferredCategory.name,
    slug: preferredCategory.slug,
    link: preferredCategory.link || `/category/${preferredCategory.slug}/`,
  };
}

function getVisibleBookmarkCategoryMeta(post) {
  const bookmarkMeta = {
    label: post.__bookmarkCategoryLabel,
    slug: post.__bookmarkCategorySlug,
    link: post.__bookmarkCategoryLink,
  };

  if (!bookmarkMeta.label && !bookmarkMeta.slug) {
    return null;
  }

  if (isHiddenMixedCategory(bookmarkMeta)) {
    return null;
  }

  return bookmarkMeta;
}

function PostCard({
  post,
  categoryLabel,
  categorySlug,
  categoryLink,
  index,
  showCategoryTag = true,
  mixedCategories = false,
}) {
  const readMoreLabel = `Read more about ${
    post.title?.rendered?.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() ||
    "this post"
  }`;
  const baseImage = getOptimizedImage(post);
  const image = baseImage?.src ? baseImage : (getItourBrandStoryImageFallback(post) || baseImage);
  const altText = image.alt;
  const excerpt = (post.excerpt?.rendered || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const bookmarkCategoryMeta = mixedCategories ? getVisibleBookmarkCategoryMeta(post) : null;
  const mixedCategoryMeta = mixedCategories ? getMixedCategoryMeta(post) : null;
  const resolvedCategoryLabel =
    (mixedCategories
      ? (
          bookmarkCategoryMeta?.label ||
          mixedCategoryMeta?.label ||
          categoryLabel ||
          ""
        )
      : (
          bookmarkCategoryMeta?.label ||
          mixedCategoryMeta?.label ||
          categoryLabel ||
          "Brand Story"
        ));
  const resolvedCategorySlug =
    bookmarkCategoryMeta?.slug ||
    mixedCategoryMeta?.slug ||
    categorySlug;
  const resolvedCategoryLink =
    bookmarkCategoryMeta?.link ||
    mixedCategoryMeta?.link ||
    categoryLink;
  const disableImageLoadEffect = resolvedCategorySlug === "brand-story";
  const tagLabel = resolvedCategoryLabel;
  const tagLink = getCategoryArchivePath(
    resolvedCategoryLink,
    resolvedCategorySlug,
    post.link
  );
  const isPriorityImage = index === 0;
  const bookmarkId = String(post.__bookmarkId ?? post.id ?? post.slug ?? post.link ?? "");
  const [isBookmarked, setIsBookmarked] = useState(() => (
    readBookmarkedPostIds().includes(bookmarkId)
  ));
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setIsBookmarked(readBookmarkedPostIds().includes(bookmarkId));
  }, [bookmarkId]);

  useEffect(() => {
    if (disableImageLoadEffect) {
      setImageLoaded(true);
      setImageFailed(false);
      return;
    }

    setImageLoaded(false);
    setImageFailed(false);
  }, [disableImageLoadEffect, image.src]);

  useEffect(() => {
    const syncBookmarks = () => {
      setIsBookmarked(readBookmarkedPostIds().includes(bookmarkId));
    };

    window.addEventListener("storage", syncBookmarks);
    window.addEventListener("pageshow", syncBookmarks);
    window.addEventListener("focus", syncBookmarks);

    return () => {
      window.removeEventListener("storage", syncBookmarks);
      window.removeEventListener("pageshow", syncBookmarks);
      window.removeEventListener("focus", syncBookmarks);
    };
  }, [bookmarkId]);

  const handleBookmarkToggle = () => {
    const currentIds = readBookmarkedPostIds();
    const nextIds = currentIds.includes(bookmarkId)
      ? currentIds.filter((id) => id !== bookmarkId)
      : [...currentIds, bookmarkId];

    writeBookmarkedPostIds(nextIds);

    if (nextIds.includes(bookmarkId)) {
      upsertBookmarkedPost(post, {
        categoryLabel: resolvedCategoryLabel,
        categorySlug: resolvedCategorySlug,
        categoryLink: resolvedCategoryLink,
      });
    } else {
      removeBookmarkedPost(bookmarkId);
    }

    setIsBookmarked(nextIds.includes(bookmarkId));
  };

  return (
    <article
      className="group relative flex h-full flex-col overflow-hidden rounded-[18px] bg-[#f8f9fa] p-[18px] transition-[background-color,color,transform] duration-300 [.trw-dark-mode_&]:border [.trw-dark-mode_&]:border-[#2a2d31] [.trw-dark-mode_&]:bg-[#1d1f22]"
      style={{ contentVisibility: "auto", containIntrinsicSize: "460px" }}
    >
      <div className="relative mb-4 w-full aspect-[1.38] overflow-hidden rounded-[20px] bg-white [.trw-dark-mode_&]:bg-[#1d1f22]">
        {showCategoryTag && tagLabel ? (
          <a
            className="absolute left-[14px] top-0 z-[2] inline-flex min-h-[28px] items-center justify-center bg-[#d02525] px-3 text-[12px] font-normal uppercase leading-[12px] text-white"
            style={{ fontFamily: "Poppins, sans-serif" }}
            href={tagLink}
          >
            {tagLabel}
          </a>
        ) : null}
        <button
          type="button"
          className={`absolute right-[8px] top-[8px] z-[1] flex h-[44px] w-[44px] items-center justify-center rounded-full border bg-white transition-colors ${
            isBookmarked ? "border-[#f70d28] text-[#f70d28]" : "border-[#ccc] text-[#12172c]"
          } hover:border-[#f70d28] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(247,13,40,0.2)] focus-visible:ring-offset-2 [.trw-dark-mode_&]:border-[#2a2d31] [.trw-dark-mode_&]:bg-[#151618] [.trw-dark-mode_&]:text-[#f87171]`}
          onClick={handleBookmarkToggle}
          aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
          aria-pressed={isBookmarked}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            fill={isBookmarked ? "currentColor" : "none"}
          >
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
        </button>
        {image?.src && !imageFailed ? (
          <>
	          <img
	  className={`absolute inset-0 h-full w-full rounded-[20px] object-cover ${disableImageLoadEffect ? "object-left-top" : "object-top"} transition-opacity duration-[180ms] ${
	    disableImageLoadEffect || imageLoaded ? "opacity-100" : "opacity-0"
	  }`}
	  src={image.src}
	  alt={altText}
	  loading={isPriorityImage ? "eager" : "lazy"}
	  fetchPriority={isPriorityImage ? "high" : undefined}
	  decoding="async"
	  sizes="(max-width: 720px) 100vw, (max-width: 1100px) 50vw, 33vw"
	  onLoad={() => setImageLoaded(true)}
	  onError={() => {
    setImageFailed(true);
    setImageLoaded(false);
  }}
/>
            {!disableImageLoadEffect && !imageLoaded ? (
              <div
                className="absolute inset-0 rounded-[20px]"
                style={{ background: "#eef1f8" }}
                aria-hidden="true"
              />
            ) : null}
          </>
        ) : (
          <div
            className="absolute inset-0 rounded-[20px]"
            style={{ background: "#eef1f8" }}
          />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-4 pt-4 text-center md:text-left">
        <a href={post.link}>
          <h2
            className="m-0 line-clamp-3 text-[26px] font-normal leading-[35px] text-black transition-colors duration-200 group-hover:text-red-600 [.trw-dark-mode_&]:text-[#f8fafc]"
            style={{ fontFamily: '"Yeseva One", Georgia, serif' }}
            dangerouslySetInnerHTML={{
              __html: truncateHTMLPreserve(post?.title?.rendered || "", 84),
            }}
          />
        </a>

        {/* Excerpt intentionally hidden to match archive styling */}

        <a
          className="mt-auto mx-auto inline-flex items-center justify-center gap-3 rounded-full border-2 border-[#d02525] bg-[#d02525] px-6 py-[10px] text-[14px] font-semibold uppercase tracking-[0.04em] text-white transition-colors duration-200 group-hover:bg-black md:ml-auto md:mr-0"
          style={{ fontFamily: "Poppins, sans-serif" }}
          href={post.link}
          aria-label={readMoreLabel}
          title={readMoreLabel}
        >
          <span className="inline-flex h-[16px] w-[16px] shrink-0 items-center justify-center" aria-hidden="true">
            <svg viewBox="0 0 105 86" focusable="false" className="h-[16px] w-[16px] fill-current rotate-[-45deg] transition-transform duration-300 group-hover:rotate-0">
              <path d="M74.98 50.91 8 50.86c-2.92 0-5.23-2.27-5.41-5.09-.11-1.82-.11-3.63-.03-5.46.13-2.9 2.48-5.05 5.45-5.05l66.74-.03-20.81-20.93c-2.31-2.32-2.32-5.79-.02-8.08l2.92-2.94c2.33-2.34 5.78-2.36 8.15.01L100.86 39c2.17 2.17 2.18 5.61 0 7.79L64.99 82.67c-2.26 2.26-5.66 2.43-7.94.19l-3.35-3.29c-2.09-2.35-2.02-5.67.3-7.85l20.96-20.97Z" />
            </svg>
          </span>
          <span>READ MORE</span>
          <span className="sr-only">{readMoreLabel}</span>
        </a>
      </div>
    </article>
  );
}

export default PostCard;
